import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, Radio, PartyPopper, Mic, Check, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { pushSupported, pushStatus, enablePush, disablePush } from "@/lib/push";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const icons = { live: Radio, event: PartyPopper, artist: Mic };

const bookingOf = (n) => (n.link || "").startsWith("/mon-espace-hote") && new URLSearchParams(n.link.split("?")[1] || "").get("booking");

function QuickReply({ bid }) {
  const [done, setDone] = useState("");
  const act = async (e, action) => {
    e.stopPropagation();
    if (action === "refuse" && !window.confirm("Refuser cette demande ?")) { return; }
    try {
      await api.post(`/residences/host/bookings/${bid}/${action}`, {});
      setDone(action === "accept" ? "Acceptée" : "Refusée");
      toast.success(action === "accept" ? "Demande acceptée — le client est prévenu" : "Demande refusée");
    } catch (err) { setDone("Déjà traitée"); toast.error(apiErr(err.response?.data?.detail)); }
  };
  if (done) { return <p data-testid={`notif-quick-done-${bid}`} className="text-xs font-bold text-[#123B4A] mt-1.5">{done}</p>; }
  return (
    <div className="flex gap-2 mt-2">
      <span role="button" tabIndex={0} data-testid={`notif-accept-${bid}`} onClick={(e) => act(e, "accept")} className="inline-flex items-center gap-1 text-xs font-bold text-white bg-emerald-600 rounded-full px-3 py-1.5"><Check className="w-3.5 h-3.5" />Accepter</span>
      <span role="button" tabIndex={0} data-testid={`notif-refuse-${bid}`} onClick={(e) => act(e, "refuse")} className="inline-flex items-center gap-1 text-xs font-bold text-red-600 border border-red-200 rounded-full px-3 py-1.5"><X className="w-3.5 h-3.5" />Refuser</span>
    </div>
  );
}

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [push, setPush] = useState("off");
  const nav = useNavigate();

  const loadCount = useCallback(() => {
    api.get("/notifications/unread_count").then((r) => setCount(r.data.count)).catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    if (pushSupported()) pushStatus().then(setPush);
    return () => clearInterval(t);
  }, [loadCount]);

  const togglePush = async () => {
    try {
      if (push === "on") { await disablePush(); setPush("off"); toast("Notifications désactivées"); }
      else { await enablePush(); setPush("on"); toast.success("Notifications activées 🔔"); }
    } catch (e) {
      if (e.message === "denied") toast.error("Autorisez les notifications dans votre navigateur.");
      else if (e.message === "unsupported") toast.error("Notifications non supportées sur cet appareil.");
      else toast.error("Impossible d'activer les notifications.");
    }
  };

  const open = async (o) => {
    if (!o) return;
    const { data } = await api.get("/notifications");
    setItems(data);
    if (count > 0) { await api.post("/notifications/read", {}); setCount(0); }
  };

  return (
    <DropdownMenu onOpenChange={open}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 text-slate-300 hover:text-white">
          <Bell className="w-5 h-5" />
          {count > 0 && <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 text-[10px] font-bold grid place-items-center rounded-full bg-[#FF5A00]">{count}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#FFFFFF] border-white/10 text-[#1A1412] w-80 max-h-96 overflow-y-auto p-0">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-2">
          <span className="font-bold text-sm">Notifications</span>
          {pushSupported() && (
            <button data-testid="push-toggle" onClick={togglePush}
              className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${push === "on" ? "bg-[#FF5A00]/20 border-[#FF5A00]/40 text-[#FF5A00]" : "border-white/15 text-slate-300 hover:bg-white/5"}`}>
              <BellRing className="w-3.5 h-3.5" /> {push === "on" ? "Activées" : "Activer"}
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-slate-400">Aucune notification.</p>
        ) : items.map((n) => {
          const Icon = icons[n.type] || Bell;
          return (
            <div role="button" tabIndex={0} key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => n.link && nav(n.link)} className="w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 flex gap-3 cursor-pointer">
              <div className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ${n.type === "live" ? "bg-[#FF5A00]/20 text-[#FF5A00]" : "bg-[#FF5A00]/20 text-[#FF5A00]"}`}><Icon className="w-4 h-4" /></div>
              <div className="min-w-0"><p className="text-sm font-semibold truncate">{n.title}</p><p className="text-xs text-slate-400 line-clamp-2">{n.body}</p>{bookingOf(n) && <QuickReply bid={bookingOf(n)} />}</div>
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
